import React, { useRef, useEffect, useState, useLayoutEffect } from "react";
import moment from "moment-timezone";
import { formatChatMessageHtml } from "../utils";

export default function AdminChatWindow({ selectedChat, currentUser, participants, userDetailsCache }) {
    const scrollRef = useRef();
    const chatBodyRef = useRef();
    const [visibleLimit, setVisibleLimit] = useState(50);
    const [prevScrollHeight, setPrevScrollHeight] = useState(0);

    // Reset limit when chat changes
    useEffect(() => {
        setVisibleLimit(50);
    }, [selectedChat?.chatId]);

    // Auto-scroll to bottom on initial load or new message (if near bottom)
    useEffect(() => {
        if (selectedChat && scrollRef.current && visibleLimit === 50) {
            scrollRef.current.scrollIntoView({ behavior: "auto" });
        }
    }, [selectedChat?.chatId]);


    // Handle scroll to top to load more
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight } = e.target;
        if (scrollTop === 0 && selectedChat?.messages?.length > visibleLimit) {
            setPrevScrollHeight(scrollHeight);
            setVisibleLimit(prev => Math.min(prev + 50, selectedChat.messages.length));
        }
    };

    // Restore scroll position after loading more
    useLayoutEffect(() => {
        if (prevScrollHeight > 0 && chatBodyRef.current) {
            const newScrollHeight = chatBodyRef.current.scrollHeight;
            chatBodyRef.current.scrollTop = newScrollHeight - prevScrollHeight;
            setPrevScrollHeight(0);
        }
    }, [visibleLimit, prevScrollHeight]);

    if (!selectedChat) {
        return (
            <div className="col-md-8 mailbox-chat">
                <div className="d-flex align-items-center justify-content-center h-100">
                    <p className="text-muted">Select a conversation to view details</p>
                </div>
            </div>
        );
    }

    // Slice messages to show only the last 'visibleLimit'
    const allMessages = selectedChat.messages || [];
    const visibleMessages = allMessages.slice(Math.max(allMessages.length - visibleLimit, 0));

    return (
        <div className="col-md-8 mailbox-chat">
            <div className="chat-content">
                <div className="chat-header p-3 border-bottom">
                    <h6>Conversation: {participants[selectedChat.chatId]?.names || "Unknown"}</h6>
                </div>
                <div
                    className="chat-body p-3"
                    style={{ height: '70vh', overflowY: 'auto', backgroundColor: '#e5ddd5' }}
                    onScroll={handleScroll}
                    ref={chatBodyRef}
                >
                    {visibleLimit < allMessages.length && (
                        <div className="text-center mb-2">
                            <small className="text-muted">Loading older messages...</small>
                        </div>
                    )}

                    {visibleMessages.map((msg, index) => {
                        const senderId = msg.senderId;
                        const participantData = participants[selectedChat.chatId];

                        let isOwner = false;
                        let senderName = senderId;

                        if (participantData) {
                            if (participantData.p1.id === senderId) {
                                isOwner = participantData.p1.role === 'Boat Owner';
                                senderName = participantData.p1.name;
                            } else if (participantData.p2.id === senderId) {
                                isOwner = participantData.p2.role === 'Boat Owner';
                                senderName = participantData.p2.name;
                            } else {
                                // Fallback if senderId doesn't match p1 or p2 (shouldn't happen ideally)
                                senderName = senderId.substring(0, 6) + "...";
                            }
                        }

                        const alignRight = isOwner;
                        const msgBg = alignRight ? '#dcf8c6' : '#ffffff';

                        return (
                            <div key={index} className={`d-flex flex-column mb-3 ${alignRight ? 'align-items-end' : 'align-items-start'}`}>
                                <div className={`p-2 rounded shadow-sm`} style={{ maxWidth: '75%', backgroundColor: msgBg }}>
                                    <small className="d-block text-muted mb-1" style={{ fontSize: '0.7rem', fontWeight: 'bold', color: alignRight ? '#075e54' : '#000' }}>
                                        {senderName} {isOwner ? '(Owner)' : '(Renter)'}
                                    </small>
                                    <span dangerouslySetInnerHTML={{ __html: formatChatMessageHtml(msg.message) }} />
                                    <div className="text-end mt-1">
                                        <small className="text-muted" style={{ fontSize: '0.65rem' }}>{moment(msg.timestamp).format('h:mm A')}</small>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={scrollRef} />
                </div>
            </div>
        </div>
    );
}
